import { IParameter } from "@/blocks/marine-forecast-chart/types/parametres-type";
import { useWindowSize } from "@/hooks/use-window-resize";
import { IMarineForecastData } from "@/schemas/marine-forecast-schema";
import { useTranslation } from "react-i18next";
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { CustomTick } from "./CustomTick";

type ChartContainerProps = {
	data: IMarineForecastData;
	firstParamsSelected: IParameter;
	secondParamsSelected?: IParameter;
	maxFirstParams?: number;
	maxSecondParams?: number;
};
export const ChartContainer = ({
	data,
	firstParamsSelected,
	secondParamsSelected,
	maxFirstParams,
	maxSecondParams,
}: ChartContainerProps) => {
	const width = useWindowSize();

	const { t } = useTranslation();

	return (
		<ResponsiveContainer width={"100%"} height={"100%"}>
			<AreaChart
				className="chart-container"
				data={data}
				margin={
					width > 950
						? { top: 40, left: 40, right: 40, bottom: 20 }
						: { top: 0, left: 0, right: 0, bottom: 0 }
				}
				style={{
					overflow: "visible",
				}}
			>
				<defs>
					{/* windspped*/}
					<linearGradient id="wind_speed_10m" x1="0" y1="0" x2="0" y2="1">
						<stop offset="5%" stopColor="#2E318B" stopOpacity={0.8} />
						<stop offset="95%" stopColor="#2E318B" stopOpacity={0.01} />
					</linearGradient>

					{/* current velocity */}
					<linearGradient id="current_speed" x1="0" y1="0" x2="0" y2="1">
						<stop offset="5%" stopColor="#B40E0E" stopOpacity={0.8} />
						<stop offset="95%" stopColor="#B40E0E" stopOpacity={0.01} />
					</linearGradient>

					{/* Mean wave period */}
					<linearGradient id="wave_period" x1="0" y1="0" x2="0" y2="1">
						<stop offset="5%" stopColor="#00FF88" stopOpacity={0.8} />
						<stop offset="95%" stopColor="#00FF88" stopOpacity={0.01} />
					</linearGradient>

					{/* Mean wave height */}
					<linearGradient id="wave_height" x1="0" y1="0" x2="0" y2="1">
						<stop offset="5%" stopColor="#F2D202" stopOpacity={0.8} />
						<stop offset="95%" stopColor="#F2D202" stopOpacity={0.01} />
					</linearGradient>

					{/* surface temp */}
					<linearGradient
						id="sea_surface_temperature"
						x1="0"
						y1="0"
						x2="0"
						y2="1"
					>
						<stop offset="5%" stopColor="#A24300" stopOpacity={0.8} />
						<stop offset="95%" stopColor="#A24300" stopOpacity={0.01} />
					</linearGradient>

					{/* tide elevation   */}
					<linearGradient id="tidal_elevation" x1="0" y1="0" x2="0" y2="1">
						<stop offset="5%" stopColor="#FF1EB7" stopOpacity={0.8} />
						<stop offset="95%" stopColor="#FF1EB7" stopOpacity={0.01} />
					</linearGradient>
				</defs>

				<XAxis
					dataKey="timestep"
					tickFormatter={(value) => {
						const date = new Date(value);
						const hours = date.getHours();
						const min = date.getMinutes();

						if (min === 1) return "";

						return hours > 9 ? hours.toString() : `0${hours}`;
					}}
					tick={(props) => (
						<CustomTick {...props} data={data} windowWidth={width} />
					)}
					tickSize={15}
					stroke="#133D61"
					strokeWidth={6}
				/>

				<YAxis
					yAxisId={firstParamsSelected.key}
					orientation="left"
					domain={[0, maxFirstParams as number]}
					tickCount={Math.floor((maxFirstParams as number) / 4) + 1}
					strokeWidth={6}
					label={{
						fill: "#133D61",
						value: `${t(firstParamsSelected.name)}`,
						position: "top",
						textAnchor: "middle",
						dy: -20,
						dx: 30,
						fontSize: 20,
					}}
					tickMargin={3}
					padding={{
						top: 10,
					}}
					tick={{
						dx: 0,
						dy: -10,
						fontSize: 20,
					}}
					tickSize={15}
					stroke="#133D61"
				/>

				{secondParamsSelected && maxSecondParams && (
					<YAxis
						yAxisId={secondParamsSelected.key}
						orientation="right"
						domain={[0, maxSecondParams]}
						tickCount={Math.floor(maxSecondParams / 2) + 1}
						label={{
							fill: "#133D61",
							value: `${t(secondParamsSelected.name)}`,
							position: "top",
							textAnchor: "middle",
							dy: -20,
							dx: -30,
							fontSize: 20,
							overflow: "visible",
						}}
						tickMargin={3}
						padding={{
							top: 10,
						}}
						tick={{
							dx: 0,
							dy: -10,
							fontSize: 20,
						}}
						strokeWidth={6}
						tickSize={15}
						stroke="#133D61"
					/>
				)}

				<Area
					yAxisId={firstParamsSelected.key}
					type="monotone"
					dataKey={firstParamsSelected.key}
					stroke={firstParamsSelected.color}
					strokeWidth={2}
					fillOpacity={1}
					fill={`url(#${firstParamsSelected.key})`}
					activeDot={false}
					dot={false}
				/>

				{secondParamsSelected?.key && (
					<Area
						strokeWidth={2}
						yAxisId={secondParamsSelected.key}
						type="monotone"
						dataKey={secondParamsSelected.key}
						stroke={secondParamsSelected.color}
						fillOpacity={0.6}
						fill={`url(#${secondParamsSelected.key})`}
						activeDot={false}
						dot={false}
					/>
				)}
			</AreaChart>
		</ResponsiveContainer>
	);
};

export default ChartContainer;
